import React from 'react';
import { useI18n } from '@/i18n';

const universityLogoModules = import.meta.glob('@/assets/university/*.{png,jpg,jpeg,webp,svg}', {
  eager: true,
  import: 'default',
}) as Record<string, string>;

const formatUniversityName = (path: string) => {
  const fileName = path.split('/').pop()?.replace(/\.[^.]+$/, '') ?? 'University';
  return fileName
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const universityItems = Object.entries(universityLogoModules)
  .sort(([pathA], [pathB]) => pathA.localeCompare(pathB))
  .map(([path, logo]) => ({
    name: formatUniversityName(path),
    logo,
  }));

const marqueeItems = [...universityItems, ...universityItems];

interface UniversityIconSectionProps {
  variant?: 'academic' | 'music';
}

const UniversityIconSection: React.FC<UniversityIconSectionProps> = ({ variant = 'academic' }) => {
  const { locale } = useI18n();
  const isZh = locale === 'zh-CN';
  return (
    <section className="bg-white">
      <div className="container mx-auto py-8 md:py-10">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-center text-lg font-semibold tracking-tight text-gray-700 md:text-xl">
            {variant === 'music' ? 'Music tutors from top universities' : isZh ? '来自顶尖大学的优质导师' : 'Top tutors from Top university'}
          </h2>

          <style>
            {`
              @keyframes university-marquee {
                from { transform: translateX(0); }
                to { transform: translateX(-50%); }
              }

              .university-marquee-track {
                width: max-content;
                animation: university-marquee 24s linear infinite;
              }

              @media (prefers-reduced-motion: reduce) {
                .university-marquee-track {
                  animation: none;
                }
              }
            `}
          </style>

          <div className="mt-6 overflow-hidden md:hidden">
            <div className="university-marquee-track flex items-center gap-4 pr-4">
              {marqueeItems.map((university, index) => (
                <div
                  key={`${university.name}-${index}`}
                  aria-hidden={index >= universityItems.length}
                  className="flex h-28 w-36 shrink-0 items-center justify-center"
                >
                  <img
                    src={university.logo}
                    alt={university.name}
                    className="h-20 w-full object-contain"
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 hidden flex-wrap items-center justify-center gap-4 md:flex md:gap-5">
            {universityItems.map((university) => (
              <div
                key={university.name}
                aria-label={`${university.name} logo`}
                className="flex h-28 w-36 items-center justify-center md:h-32 md:w-44"
              >
                <img
                  src={university.logo}
                  alt={university.name}
                  className="h-20 w-full object-contain md:h-24"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default UniversityIconSection;
